import type { Row } from "./types.js";

/**
 * 캐릭터 영역(CharactersView / CharacterEditor) UI 문자열.
 * 키 접두사: char.* / charEd.*
 */
export const characterMessages = {
  // ── CharactersView (char.*) ───────────────────────────────────────────
  "char.chatTitle": {
    ko: "{name}와의 대화", "en-US": "Chat with {name}", ja: "{name}との会話", zh: "与{name}的对话",
    fr: "Conversation avec {name}", de: "Chat mit {name}", es: "Chat con {name}", it: "Chat con {name}",
    pt: "Conversa com {name}", "pt-BR": "Conversa com {name}", ru: "Чат с {name}",
    ar: "محادثة مع {name}", hi: "{name} के साथ चैट", id: "Obrolan dengan {name}",
  },
  "char.characterCount": {
    ko: "{count}명의 캐릭터", "en-US": "{count} characters", ja: "{count}体のキャラクター", zh: "{count}个角色",
    fr: "{count} personnages", de: "{count} Charaktere", es: "{count} personajes", it: "{count} personaggi",
    pt: "{count} personagens", "pt-BR": "{count} personagens", ru: "Персонажей: {count}",
    ar: "{count} شخصية", hi: "{count} पात्र", id: "{count} karakter",
  },
  "char.importTitle": {
    ko: "Risu/SillyTavern 카드 가져오기 (JSON / PNG)", "en-US": "Import Risu/SillyTavern card (JSON / PNG)",
    ja: "Risu/SillyTavern カードを取り込み (JSON / PNG)", zh: "导入 Risu/SillyTavern 卡片（JSON / PNG）",
    fr: "Importer une carte Risu/SillyTavern (JSON / PNG)", de: "Risu/SillyTavern-Karte importieren (JSON / PNG)",
    es: "Importar tarjeta Risu/SillyTavern (JSON / PNG)", it: "Importa carta Risu/SillyTavern (JSON / PNG)",
    pt: "Importar cartão Risu/SillyTavern (JSON / PNG)", "pt-BR": "Importar cartão Risu/SillyTavern (JSON / PNG)",
    ru: "Импорт карты Risu/SillyTavern (JSON / PNG)", ar: "استيراد بطاقة Risu/SillyTavern (JSON / PNG)",
    hi: "Risu/SillyTavern कार्ड आयात करें (JSON / PNG)", id: "Impor kartu Risu/SillyTavern (JSON / PNG)",
  },
  "char.importing": {
    ko: "가져오는 중...", "en-US": "Importing...", ja: "取り込み中...", zh: "导入中...",
    fr: "Importation...", de: "Wird importiert...", es: "Importando...", it: "Importazione...",
    pt: "A importar...", "pt-BR": "Importando...", ru: "Импорт...", ar: "جارٍ الاستيراد...",
    hi: "आयात हो रहा है...", id: "Mengimpor...",
  },
  "char.importCard": {
    ko: "카드 가져오기", "en-US": "Import card", ja: "カードを取り込み", zh: "导入卡片",
    fr: "Importer une carte", de: "Karte importieren", es: "Importar tarjeta", it: "Importa carta",
    pt: "Importar cartão", "pt-BR": "Importar cartão", ru: "Импорт карты",
    ar: "استيراد بطاقة", hi: "कार्ड आयात करें", id: "Impor kartu",
  },
  "char.newCharacter": {
    ko: "새 캐릭터", "en-US": "New character", ja: "新しいキャラクター", zh: "新建角色",
    fr: "Nouveau personnage", de: "Neuer Charakter", es: "Nuevo personaje", it: "Nuovo personaggio",
    pt: "Nova personagem", "pt-BR": "Novo personagem", ru: "Новый персонаж",
    ar: "شخصية جديدة", hi: "नया पात्र", id: "Karakter baru",
  },
  "char.cardsImported": {
    ko: "{count}개 카드 가져옴.", "en-US": "Imported {count} card(s).", ja: "{count}枚のカードを取り込みました。",
    zh: "已导入{count}张卡片。", fr: "{count} carte(s) importée(s).", de: "{count} Karte(n) importiert.",
    es: "{count} tarjeta(s) importada(s).", it: "{count} carta/e importata/e.", pt: "{count} cartão(ões) importado(s).",
    "pt-BR": "{count} cartão(ões) importado(s).", ru: "Импортировано карт: {count}.",
    ar: "تم استيراد {count} بطاقة.", hi: "{count} कार्ड आयात किए गए।", id: "{count} kartu diimpor.",
  },
  "char.unmappedFields": {
    ko: "{name}: 미지원 필드 {count}개 보존됨", "en-US": "{name}: {count} unsupported field(s) preserved",
    ja: "{name}：未対応フィールド{count}件を保持", zh: "{name}：保留了{count}个不支持的字段",
    fr: "{name} : {count} champ(s) non pris en charge conservé(s)", de: "{name}: {count} nicht unterstützte(s) Feld(er) erhalten",
    es: "{name}: {count} campo(s) no compatible(s) conservado(s)", it: "{name}: {count} campo/i non supportato/i conservato/i",
    pt: "{name}: {count} campo(s) não suportado(s) preservado(s)", "pt-BR": "{name}: {count} campo(s) não suportado(s) preservado(s)",
    ru: "{name}: сохранено неподдерживаемых полей — {count}", ar: "{name}: تم الحفاظ على {count} حقل غير مدعوم",
    hi: "{name}: {count} असमर्थित फ़ील्ड सुरक्षित रखे गए", id: "{name}: {count} bidang tak didukung dipertahankan",
  },
  "char.searchPlaceholder": {
    ko: "이름·설명·태그 검색", "en-US": "Search name, description, tags", ja: "名前・説明・タグを検索",
    zh: "搜索名称、描述、标签", fr: "Rechercher nom, description, tags", de: "Name, Beschreibung, Tags suchen",
    es: "Buscar nombre, descripción, etiquetas", it: "Cerca nome, descrizione, tag",
    pt: "Pesquisar nome, descrição, etiquetas", "pt-BR": "Buscar nome, descrição, tags",
    ru: "Поиск по имени, описанию, тегам", ar: "ابحث بالاسم أو الوصف أو الوسوم",
    hi: "नाम, विवरण, टैग खोजें", id: "Cari nama, deskripsi, tag",
  },
  "char.allTags": {
    ko: "전체", "en-US": "All", ja: "すべて", zh: "全部", fr: "Tous", de: "Alle",
    es: "Todos", it: "Tutti", pt: "Todos", "pt-BR": "Todos", ru: "Все",
    ar: "الكل", hi: "सभी", id: "Semua",
  },
  "char.noTags": {
    ko: "태그 없음", "en-US": "No tags", ja: "タグなし", zh: "无标签", fr: "Aucun tag", de: "Keine Tags",
    es: "Sin etiquetas", it: "Nessun tag", pt: "Sem etiquetas", "pt-BR": "Sem tags", ru: "Без тегов",
    ar: "بدون وسوم", hi: "कोई टैग नहीं", id: "Tanpa tag",
  },
  "char.emptyNoChars": {
    ko: "캐릭터가 없어요. “카드 가져오기”로 불러오거나 대화 화면에서 “새 대화”를 누르면 기본 캐릭터가 생성돼요.",
    "en-US": "No characters yet. Import one with “Import card,” or press “New chat” on the chat screen to create a default character.",
    ja: "キャラクターがいません。「カードを取り込み」で読み込むか、チャット画面で「新しい会話」を押すとデフォルトキャラクターが作成されます。",
    zh: "还没有角色。用“导入卡片”载入，或在对话界面点击“新建对话”以生成默认角色。",
    fr: "Aucun personnage pour l'instant. Importez-en un avec « Importer une carte », ou appuyez sur « Nouvelle conversation » dans l'écran de chat pour créer un personnage par défaut.",
    de: "Noch keine Charaktere. Importiere einen über „Karte importieren“ oder tippe im Chat-Bildschirm auf „Neuer Chat“, um einen Standardcharakter zu erstellen.",
    es: "Aún no hay personajes. Importa uno con «Importar tarjeta» o pulsa «Nuevo chat» en la pantalla de chat para crear un personaje predeterminado.",
    it: "Ancora nessun personaggio. Importane uno con «Importa carta» oppure premi «Nuova chat» nella schermata di chat per creare un personaggio predefinito.",
    pt: "Ainda não há personagens. Importa uma com «Importar cartão» ou prime «Nova conversa» no ecrã de conversa para criar uma personagem predefinida.",
    "pt-BR": "Ainda não há personagens. Importe um com “Importar cartão” ou toque em “Nova conversa” na tela de conversa para criar um personagem padrão.",
    ru: "Персонажей пока нет. Импортируйте через «Импорт карты» или нажмите «Новый чат» на экране чата, чтобы создать персонажа по умолчанию.",
    ar: "لا توجد شخصيات بعد. استورد واحدة عبر «استيراد بطاقة»، أو اضغط «محادثة جديدة» في شاشة المحادثة لإنشاء شخصية افتراضية.",
    hi: "अभी कोई पात्र नहीं है। “कार्ड आयात करें” से लाएँ, या चैट स्क्रीन पर “नई चैट” दबाने पर एक डिफ़ॉल्ट पात्र बन जाएगा।",
    id: "Belum ada karakter. Impor satu lewat “Impor kartu,” atau tekan “Obrolan baru” di layar obrolan untuk membuat karakter bawaan.",
  },
  "char.emptyNoResults": {
    ko: "검색 결과가 없어요. 다른 검색어나 태그를 시도해 보세요.",
    "en-US": "No results. Try a different search or tag.",
    ja: "検索結果がありません。別のキーワードやタグをお試しください。",
    zh: "没有结果。请尝试其他搜索词或标签。",
    fr: "Aucun résultat. Essayez une autre recherche ou un autre tag.",
    de: "Keine Ergebnisse. Versuche eine andere Suche oder einen anderen Tag.",
    es: "Sin resultados. Prueba con otra búsqueda o etiqueta.",
    it: "Nessun risultato. Prova con un'altra ricerca o un altro tag.",
    pt: "Sem resultados. Experimenta outra pesquisa ou etiqueta.",
    "pt-BR": "Nenhum resultado. Tente outra busca ou tag.",
    ru: "Ничего не найдено. Попробуйте другой запрос или тег.",
    ar: "لا توجد نتائج. جرّب بحثًا أو وسمًا آخر.",
    hi: "कोई परिणाम नहीं। कोई दूसरी खोज या टैग आज़माएँ।",
    id: "Tidak ada hasil. Coba pencarian atau tag lain.",
  },
  "char.startChatWith": {
    ko: "{name}와 대화 시작", "en-US": "Start a chat with {name}", ja: "{name}との会話を開始",
    zh: "开始与{name}的对话", fr: "Démarrer une conversation avec {name}", de: "Chat mit {name} starten",
    es: "Iniciar chat con {name}", it: "Avvia una chat con {name}", pt: "Iniciar conversa com {name}",
    "pt-BR": "Iniciar conversa com {name}", ru: "Начать чат с {name}", ar: "بدء محادثة مع {name}",
    hi: "{name} के साथ चैट शुरू करें", id: "Mulai obrolan dengan {name}",
  },
  "char.startChat": {
    ko: "대화 시작", "en-US": "Start chat", ja: "会話を開始", zh: "开始对话",
    fr: "Démarrer la conversation", de: "Chat starten", es: "Iniciar chat", it: "Avvia chat",
    pt: "Iniciar conversa", "pt-BR": "Iniciar conversa", ru: "Начать чат",
    ar: "بدء المحادثة", hi: "चैट शुरू करें", id: "Mulai obrolan",
  },
  "char.uploading": {
    ko: "업로드...", "en-US": "Uploading...", ja: "アップロード中...", zh: "上传中...",
    fr: "Envoi...", de: "Wird hochgeladen...", es: "Subiendo...", it: "Caricamento...",
    pt: "A enviar...", "pt-BR": "Enviando...", ru: "Загрузка...", ar: "جارٍ الرفع...",
    hi: "अपलोड हो रहा है...", id: "Mengunggah...",
  },
  "char.avatar": {
    ko: "아바타", "en-US": "Avatar", ja: "アバター", zh: "头像", fr: "Avatar", de: "Avatar",
    es: "Avatar", it: "Avatar", pt: "Avatar", "pt-BR": "Avatar", ru: "Аватар",
    ar: "الصورة الرمزية", hi: "अवतार", id: "Avatar",
  },
  "char.exportTitle": {
    ko: "CCv3 JSON 카드로 내보내기", "en-US": "Export as CCv3 JSON card", ja: "CCv3 JSON カードとして書き出し",
    zh: "导出为 CCv3 JSON 卡片", fr: "Exporter en carte CCv3 JSON", de: "Als CCv3-JSON-Karte exportieren",
    es: "Exportar como tarjeta JSON CCv3", it: "Esporta come carta JSON CCv3", pt: "Exportar como cartão JSON CCv3",
    "pt-BR": "Exportar como cartão JSON CCv3", ru: "Экспорт в карту CCv3 JSON",
    ar: "تصدير كبطاقة CCv3 JSON", hi: "CCv3 JSON कार्ड के रूप में निर्यात करें", id: "Ekspor sebagai kartu JSON CCv3",
  },
  "char.deleteConfirm": {
    ko: "\"{name}\" 캐릭터를 삭제할까요?", "en-US": "Delete character \"{name}\"?",
    ja: "キャラクター「{name}」を削除しますか？", zh: "删除角色“{name}”？",
    fr: "Supprimer le personnage « {name} » ?", de: "Charakter „{name}“ löschen?",
    es: "¿Eliminar el personaje «{name}»?", it: "Eliminare il personaggio «{name}»?",
    pt: "Eliminar a personagem \"{name}\"?", "pt-BR": "Excluir o personagem \"{name}\"?",
    ru: "Удалить персонажа «{name}»?", ar: "حذف الشخصية \"{name}\"؟",
    hi: "पात्र \"{name}\" हटाएँ?", id: "Hapus karakter \"{name}\"?",
  },
  "char.greetingPickerTitle": {
    ko: "{name} · 인사말 선택", "en-US": "{name} · Choose a greeting", ja: "{name} · 挨拶を選択",
    zh: "{name} · 选择问候语", fr: "{name} · Choisir un message d'accueil", de: "{name} · Begrüßung wählen",
    es: "{name} · Elegir un saludo", it: "{name} · Scegli un saluto", pt: "{name} · Escolher uma saudação",
    "pt-BR": "{name} · Escolher uma saudação", ru: "{name} · Выбор приветствия",
    ar: "{name} · اختر تحية", hi: "{name} · अभिवादन चुनें", id: "{name} · Pilih sapaan",
  },
  "char.close": {
    ko: "닫기", "en-US": "Close", ja: "閉じる", zh: "关闭", fr: "Fermer", de: "Schließen",
    es: "Cerrar", it: "Chiudi", pt: "Fechar", "pt-BR": "Fechar", ru: "Закрыть",
    ar: "إغلاق", hi: "बंद करें", id: "Tutup",
  },
  "char.defaultGreeting": {
    ko: "기본 인사말", "en-US": "Default greeting", ja: "デフォルトの挨拶", zh: "默认问候语",
    fr: "Message d'accueil par défaut", de: "Standardbegrüßung", es: "Saludo predeterminado",
    it: "Saluto predefinito", pt: "Saudação predefinida", "pt-BR": "Saudação padrão",
    ru: "Приветствие по умолчанию", ar: "التحية الافتراضية", hi: "डिफ़ॉल्ट अभिवादन", id: "Sapaan bawaan",
  },
  "char.altGreeting": {
    ko: "대체 인사말 {n}", "en-US": "Alternate greeting {n}", ja: "代替の挨拶 {n}", zh: "备用问候语 {n}",
    fr: "Message d'accueil alternatif {n}", de: "Alternative Begrüßung {n}", es: "Saludo alternativo {n}",
    it: "Saluto alternativo {n}", pt: "Saudação alternativa {n}", "pt-BR": "Saudação alternativa {n}",
    ru: "Альтернативное приветствие {n}", ar: "تحية بديلة {n}", hi: "वैकल्पिक अभिवादन {n}", id: "Sapaan alternatif {n}",
  },

  // ── CharacterEditor (charEd.*) ────────────────────────────────────────
  "charEd.editTitle": {
    ko: "캐릭터 편집", "en-US": "Edit character", ja: "キャラクターを編集", zh: "编辑角色",
    fr: "Modifier le personnage", de: "Charakter bearbeiten", es: "Editar personaje", it: "Modifica personaggio",
    pt: "Editar personagem", "pt-BR": "Editar personagem", ru: "Изменить персонажа",
    ar: "تعديل الشخصية", hi: "पात्र संपादित करें", id: "Edit karakter",
  },
  "charEd.errNameRequired": {
    ko: "이름은 필수입니다.", "en-US": "Name is required.", ja: "名前は必須です。", zh: "名称为必填项。",
    fr: "Le nom est obligatoire.", de: "Name ist erforderlich.", es: "El nombre es obligatorio.",
    it: "Il nome è obbligatorio.", pt: "O nome é obrigatório.", "pt-BR": "O nome é obrigatório.",
    ru: "Имя обязательно.", ar: "الاسم مطلوب.", hi: "नाम आवश्यक है।", id: "Nama wajib diisi.",
  },
  "charEd.fieldName": {
    ko: "이름 *", "en-US": "Name *", ja: "名前 *", zh: "名称 *", fr: "Nom *", de: "Name *",
    es: "Nombre *", it: "Nome *", pt: "Nome *", "pt-BR": "Nome *", ru: "Имя *",
    ar: "الاسم *", hi: "नाम *", id: "Nama *",
  },
  "charEd.namePlaceholder": {
    ko: "캐릭터 이름", "en-US": "Character name", ja: "キャラクター名", zh: "角色名称",
    fr: "Nom du personnage", de: "Charaktername", es: "Nombre del personaje", it: "Nome del personaggio",
    pt: "Nome da personagem", "pt-BR": "Nome do personagem", ru: "Имя персонажа",
    ar: "اسم الشخصية", hi: "पात्र का नाम", id: "Nama karakter",
  },
  "charEd.fieldDescription": {
    ko: "설명 (description)", "en-US": "Description", ja: "説明 (description)", zh: "描述 (description)",
    fr: "Description", de: "Beschreibung", es: "Descripción", it: "Descrizione",
    pt: "Descrição", "pt-BR": "Descrição", ru: "Описание",
    ar: "الوصف (description)", hi: "विवरण (description)", id: "Deskripsi",
  },
  "charEd.hintRoqeetInline": {
    ko: "@캐릭터, @유저, @(식), @은는 등 RoQeet 인라인 값 사용 가능", "en-US": "Supports RoQeet inline values like @char, @user, @(expression)",
    ja: "@char、@user、@(式) などの RoQeet インライン値が使えます", zh: "可使用 @char、@user、@(表达式) 等 RoQeet 内联值",
    fr: "Prend en charge les valeurs RoQeet comme @char, @user, @(expression)", de: "Unterstützt RoQeet-Inlinewerte wie @char, @user, @(Ausdruck)",
    es: "Admite valores RoQeet como @char, @user, @(expresión)", it: "Supporta valori RoQeet come @char, @user, @(espressione)",
    pt: "Suporta valores RoQeet como @char, @user, @(expressão)", "pt-BR": "Suporta valores RoQeet como @char, @user, @(expressão)",
    ru: "Поддерживает inline-значения RoQeet: @char, @user, @(выражение)", ar: "يدعم قيم RoQeet مثل @char و@user و@(expression)",
    hi: "@char, @user, @(expression) जैसे RoQeet inline values समर्थित", id: "Mendukung nilai inline RoQeet seperti @char, @user, @(expression)",
  },
  "charEd.fieldPersonality": {
    ko: "성격 (personality)", "en-US": "Personality", ja: "性格 (personality)", zh: "性格 (personality)",
    fr: "Personnalité", de: "Persönlichkeit", es: "Personalidad", it: "Personalità",
    pt: "Personalidade", "pt-BR": "Personalidade", ru: "Характер",
    ar: "الشخصية (personality)", hi: "व्यक्तित्व (personality)", id: "Kepribadian",
  },
  "charEd.fieldScenario": {
    ko: "시나리오 (scenario)", "en-US": "Scenario", ja: "シナリオ (scenario)", zh: "场景 (scenario)",
    fr: "Scénario", de: "Szenario", es: "Escenario", it: "Scenario",
    pt: "Cenário", "pt-BR": "Cenário", ru: "Сценарий",
    ar: "السيناريو (scenario)", hi: "परिदृश्य (scenario)", id: "Skenario",
  },
  "charEd.fieldFirstMessage": {
    ko: "첫 인사말 (first message)", "en-US": "First message", ja: "最初の挨拶 (first message)",
    zh: "首条消息 (first message)", fr: "Premier message", de: "Erste Nachricht",
    es: "Primer mensaje", it: "Primo messaggio", pt: "Primeira mensagem", "pt-BR": "Primeira mensagem",
    ru: "Первое сообщение", ar: "الرسالة الأولى (first message)", hi: "पहला संदेश (first message)",
    id: "Pesan pertama",
  },
  "charEd.fieldAltGreetings": {
    ko: "대체 인사말", "en-US": "Alternate greetings", ja: "代替の挨拶", zh: "备用问候语",
    fr: "Messages d'accueil alternatifs", de: "Alternative Begrüßungen", es: "Saludos alternativos",
    it: "Saluti alternativi", pt: "Saudações alternativas", "pt-BR": "Saudações alternativas",
    ru: "Альтернативные приветствия", ar: "تحيات بديلة", hi: "वैकल्पिक अभिवादन", id: "Sapaan alternatif",
  },
  "charEd.hintAltGreetings": {
    ko: "여러 개는 한 줄에 --- 로 구분", "en-US": "Separate multiple with --- on its own line",
    ja: "複数は --- だけの行で区切る", zh: "多个用单独一行的 --- 分隔",
    fr: "Séparez-les par --- sur une ligne dédiée", de: "Mehrere mit --- in einer eigenen Zeile trennen",
    es: "Separa varios con --- en su propia línea", it: "Separa più voci con --- su una riga dedicata",
    pt: "Separa vários com --- numa linha só", "pt-BR": "Separe vários com --- em uma linha própria",
    ru: "Несколько разделяйте строкой с ---", ar: "افصل بين عدّة بسطر يحتوي --- فقط",
    hi: "कई हों तो अलग पंक्ति में --- से अलग करें", id: "Pisahkan beberapa dengan --- di baris tersendiri",
  },
  "charEd.fieldMessageExample": {
    ko: "대화 예시 (message example)", "en-US": "Message example", ja: "会話例 (message example)",
    zh: "对话示例 (message example)", fr: "Exemple de message", de: "Nachrichtenbeispiel",
    es: "Ejemplo de mensaje", it: "Esempio di messaggio", pt: "Exemplo de mensagem", "pt-BR": "Exemplo de mensagem",
    ru: "Пример сообщения", ar: "مثال رسالة (message example)", hi: "संदेश उदाहरण (message example)",
    id: "Contoh pesan",
  },
  "charEd.fieldSystemPrompt": {
    ko: "시스템 프롬프트 override", "en-US": "System prompt override", ja: "システムプロンプトの上書き",
    zh: "系统提示词覆盖", fr: "Remplacement du prompt système", de: "System-Prompt-Überschreibung",
    es: "Anulación del prompt del sistema", it: "Override del prompt di sistema", pt: "Substituição do prompt do sistema",
    "pt-BR": "Substituição do prompt do sistema", ru: "Переопределение системного промпта",
    ar: "تجاوز موجّه النظام", hi: "सिस्टम प्रॉम्प्ट ओवरराइड", id: "Penggantian prompt sistem",
  },
  "charEd.hintSystemPrompt": {
    ko: "비우면 프리셋의 기본 시스템 프롬프트 사용", "en-US": "Leave empty to use the preset's default system prompt",
    ja: "空欄ならプリセットの既定システムプロンプトを使用", zh: "留空则使用预设的默认系统提示词",
    fr: "Laissez vide pour utiliser le prompt système par défaut du préréglage",
    de: "Leer lassen, um den Standard-System-Prompt des Presets zu verwenden",
    es: "Déjalo vacío para usar el prompt del sistema predeterminado del preajuste",
    it: "Lascia vuoto per usare il prompt di sistema predefinito del preset",
    pt: "Deixa vazio para usar o prompt do sistema predefinido da predefinição",
    "pt-BR": "Deixe vazio para usar o prompt do sistema padrão da predefinição",
    ru: "Оставьте пустым, чтобы использовать системный промпт пресета по умолчанию",
    ar: "اتركه فارغًا لاستخدام موجّه النظام الافتراضي للإعداد المسبق",
    hi: "खाली छोड़ें तो प्रीसेट का डिफ़ॉल्ट सिस्टम प्रॉम्प्ट इस्तेमाल होगा",
    id: "Kosongkan untuk memakai prompt sistem bawaan praatur",
  },
  "charEd.fieldTags": {
    ko: "태그", "en-US": "Tags", ja: "タグ", zh: "标签", fr: "Tags", de: "Tags",
    es: "Etiquetas", it: "Tag", pt: "Etiquetas", "pt-BR": "Tags", ru: "Теги",
    ar: "الوسوم", hi: "टैग", id: "Tag",
  },
  "charEd.hintTags": {
    ko: "쉼표로 구분", "en-US": "Separate with commas", ja: "カンマ区切り", zh: "用逗号分隔",
    fr: "Séparez par des virgules", de: "Mit Kommas trennen", es: "Separa con comas", it: "Separa con virgole",
    pt: "Separa por vírgulas", "pt-BR": "Separe por vírgulas", ru: "Разделяйте запятыми",
    ar: "افصل بفواصل", hi: "अल्पविराम से अलग करें", id: "Pisahkan dengan koma",
  },
  "charEd.tagsPlaceholder": {
    ko: "판타지, 친절함", "en-US": "Fantasy, kind", ja: "ファンタジー, 優しい", zh: "奇幻, 善良",
    fr: "Fantastique, gentil", de: "Fantasy, freundlich", es: "Fantasía, amable", it: "Fantasy, gentile",
    pt: "Fantasia, gentil", "pt-BR": "Fantasia, gentil", ru: "Фэнтези, добрый",
    ar: "خيال، لطيف", hi: "फंतासी, दयालु", id: "Fantasi, baik hati",
  },
  "charEd.fieldLorebook": {
    ko: "로어북 (세계관)", "en-US": "Lorebook (worldbuilding)", ja: "ロアブック（世界観）",
    zh: "设定集（世界观）", fr: "Codex (univers)", de: "Lorebook (Worldbuilding)",
    es: "Compendio (ambientación)", it: "Codex (worldbuilding)", pt: "Compêndio (universo)",
    "pt-BR": "Compêndio (universo)", ru: "Лорбук (мир)", ar: "كتاب المعرفة (بناء العالم)",
    hi: "लोरबुक (विश्व-निर्माण)", id: "Lorebook (dunia cerita)",
  },
  "charEd.hintLorebook": {
    ko: "로어북 화면에서 만든 것을 연결", "en-US": "Link one created on the Lorebooks screen",
    ja: "ロアブック画面で作成したものを連携", zh: "关联在设定集界面创建的内容",
    fr: "Liez-en un créé dans l'écran Codex", de: "Verknüpfe eines aus dem Lorebook-Bildschirm",
    es: "Vincula uno creado en la pantalla de Compendios", it: "Collega uno creato nella schermata Codex",
    pt: "Associa um criado no ecrã de Compêndios", "pt-BR": "Vincule um criado na tela de Compêndios",
    ru: "Привяжите созданный на экране лорбуков", ar: "اربط واحدًا أنشأته في شاشة كتب المعرفة",
    hi: "लोरबुक स्क्रीन पर बनाया हुआ जोड़ें", id: "Tautkan yang dibuat di layar Lorebook",
  },
  "charEd.lorebookNone": {
    ko: "연결 안 함", "en-US": "Not linked", ja: "連携しない", zh: "不关联",
    fr: "Non lié", de: "Nicht verknüpft", es: "Sin vincular", it: "Non collegato",
    pt: "Não associado", "pt-BR": "Não vinculado", ru: "Не привязано",
    ar: "غير مرتبط", hi: "जुड़ा नहीं", id: "Tidak ditautkan",
  },
  "charEd.fieldScripts": {
    ko: "스크립트", "en-US": "Scripts", ja: "スクリプト", zh: "脚本", fr: "Scripts", de: "Skripte",
    es: "Scripts", it: "Script", pt: "Scripts", "pt-BR": "Scripts", ru: "Скрипты",
    ar: "النصوص البرمجية", hi: "स्क्रिप्ट", id: "Skrip",
  },
  "charEd.hintScripts": {
    ko: "설정에서 만든 정규식·JS 스크립트를 연결", "en-US": "Link regex/JS scripts created in Settings",
    ja: "設定で作成した正規表現・JS スクリプトを連携", zh: "关联在设置中创建的正则/JS 脚本",
    fr: "Liez les scripts regex/JS créés dans les Paramètres", de: "Verknüpfe in den Einstellungen erstellte Regex-/JS-Skripte",
    es: "Vincula scripts de regex/JS creados en Ajustes", it: "Collega gli script regex/JS creati nelle Impostazioni",
    pt: "Associa scripts regex/JS criados nas Definições", "pt-BR": "Vincule scripts de regex/JS criados nas Configurações",
    ru: "Привяжите regex/JS-скрипты из настроек", ar: "اربط نصوص regex/JS التي أنشأتها في الإعدادات",
    hi: "सेटिंग्स में बनाए रेगेक्स/JS स्क्रिप्ट जोड़ें", id: "Tautkan skrip regex/JS yang dibuat di Pengaturan",
  },
  "charEd.noScripts": {
    ko: "스크립트가 없어요. 설정 → 정규식/JS 스크립트에서 먼저 만드세요.",
    "en-US": "No scripts yet. Create one in Settings → Regex/JS scripts first.",
    ja: "スクリプトがありません。設定 → 正規表現/JS スクリプトで先に作成してください。",
    zh: "还没有脚本。请先在 设置 → 正则/JS 脚本 中创建。",
    fr: "Aucun script pour l'instant. Créez-en un d'abord dans Paramètres → Scripts regex/JS.",
    de: "Noch keine Skripte. Erstelle zuerst eins unter Einstellungen → Regex-/JS-Skripte.",
    es: "Aún no hay scripts. Crea uno primero en Ajustes → Scripts de regex/JS.",
    it: "Ancora nessuno script. Creane prima uno in Impostazioni → Script regex/JS.",
    pt: "Ainda não há scripts. Cria um primeiro em Definições → Scripts regex/JS.",
    "pt-BR": "Ainda não há scripts. Crie um primeiro em Configurações → Scripts de regex/JS.",
    ru: "Скриптов пока нет. Сначала создайте его в Настройки → Regex/JS-скрипты.",
    ar: "لا توجد نصوص بعد. أنشئ واحدًا أولًا من الإعدادات ← نصوص regex/JS.",
    hi: "अभी कोई स्क्रिप्ट नहीं है। पहले सेटिंग्स → रेगेक्स/JS स्क्रिप्ट में बनाएँ।",
    id: "Belum ada skrip. Buat dulu di Pengaturan → Skrip regex/JS.",
  },
  "charEd.langRegex": {
    ko: "정규식", "en-US": "Regex", ja: "正規表現", zh: "正则", fr: "Regex", de: "Regex",
    es: "Regex", it: "Regex", pt: "Regex", "pt-BR": "Regex", ru: "Regex",
    ar: "Regex", hi: "रेगेक्स", id: "Regex",
  },
} satisfies Record<string, Row>;
